import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AthleticLiveFirebaseApiService } from '@athleticnet/athletic-live-api';
import { MeetList } from '@athleticnet/athletic-live-api-models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../shared/anet/auth/auth.service';
import { AthleticUser } from '../shared/anet/models/athletic-user.interface';
import { CurrentMeetService } from '../shared/services/current-meet.service';
import { IsDesktopService } from '../shared/services/is-desktop.service';
import { IsSidebarOpenService } from '../shared/services/is-sidebar-open.service';
import { SiteFooterComponent } from '../shared/site-footer/site-footer.component';
import { FooterMenuComponent } from './footer-menu/footer-menu.component';
import { MobileSidebarComponent } from './mobile-sidebar/mobile-sidebar.component';
import { SidebarComponent } from './sidebar/sidebar.component';
@Component({
  selector: 'app-meet',
  templateUrl: './meet.component.html',
  styleUrls: ['./meet.component.scss'],
  standalone: true,
  imports: [
    SidebarComponent,
    RouterOutlet,
    FooterMenuComponent,
    AsyncPipe,
    MobileSidebarComponent,
    FontAwesomeModule,
    SiteFooterComponent,
  ],
})
export class MeetComponent implements OnInit, OnDestroy {
  @ViewChild('container') container!: ElementRef;
  meetSubscription!: Subscription;
  eventSubscription!: Subscription;
  liveRunEventSubscription!: Subscription;
  liveFieldEventSubscription!: Subscription;
  isSidebarOpenServiceSubscription!: Subscription;
  meetId!: number;
  currentMeet!: MeetList;
  desktop$!: Observable<boolean>;
  initialMeetFollowLoad: boolean = false;
  loaded: boolean = false;
  loading: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private currentMeetService: CurrentMeetService,
    private fb: AthleticLiveFirebaseApiService,
    private offcanvasService: NgbOffcanvas,
    private isSidebarOpenService: IsSidebarOpenService,
    private isDesktopService: IsDesktopService,
    private authService: AuthService,
    private router: Router,
  ) {
    //this.currentMeetService.setCurrentSection('home');
    this.desktop$ = this.isDesktopService.isDesktop;
    this.isSidebarOpenServiceSubscription = this.isSidebarOpenService.isSidebarOpen.subscribe((isSidebarOpen) => {
      //meetContainer--left--closed
      if (this.container) {
        if (isSidebarOpen) {
          this.container.nativeElement.classList.remove('meetContainer--left--closed');
        } else {
          this.container.nativeElement.classList.add('meetContainer--left--closed');
        }
      }
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.route.params.subscribe(async (params) => {
      if (this.meetId !== +params['meetId']) {
        this.meetId = +params['meetId'];

        if (isNaN(this.meetId)) {
          this.router.navigate(['/']);
        }

        if (this.loaded) {
          await this.unsetMeet();
        }

        this.meetSubscription = combineLatest([
          this.fb.getMeetList(this.meetId),
          this.authService.updateUser,
        ]).subscribe(async ([meet, user]: [MeetList, AthleticUser | null]) => {
          const scope = Sentry.getCurrentScope();
          if (meet && Object.keys(meet).length > 2) {
            await this.currentMeetService.setMeet(meet);
            this.currentMeet = meet;
            this.loading = false;
            if (user) {
              scope.setUser({ id: user.userId.toString() });
            }
            if (meet) {
              scope.setExtra('meetId', meet.id.toString());
            }
            if (meet && user && user.userId > 0 && !this.initialMeetFollowLoad) {
              this.initialMeetFollowLoad = true;
              await this.authService.setNotices(this.meetId);
              await this.authService.setFollowInfo(meet);
            }
          } else {
            this.router.navigate(['/']);
          }
        });
        this.eventSubscription = this.fb.getMeetEventSummary(+this.meetId).subscribe((data) => {
          this.currentMeetService.setEvents(data);
        });
        this.liveRunEventSubscription = this.fb.getLiveRunEvents(+this.meetId).subscribe((data) => {
          this.currentMeetService.setLiveRunEvents(data);
        });
        this.liveFieldEventSubscription = this.fb.getLiveFieldEvents(+this.meetId).subscribe((data) => {
          this.currentMeetService.setLiveFieldEvents(data);
        });
        this.loaded = true;
      }
    });
  }

  async ngOnDestroy() {
    await this.unsetMeet();
    if (this.isSidebarOpenServiceSubscription) {
      this.isSidebarOpenServiceSubscription.unsubscribe();
    }
  }

  async unsetMeet() {
    //console.log('unset meet');
    if (this.meetSubscription) {
      this.meetSubscription.unsubscribe();
    }
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
    if (this.liveRunEventSubscription) {
      this.liveRunEventSubscription.unsubscribe();
    }
    if (this.liveFieldEventSubscription) {
      this.liveFieldEventSubscription.unsubscribe();
    }
    if (this.isSidebarOpenServiceSubscription) {
      this.isSidebarOpenServiceSubscription.unsubscribe();
    }
    await this.currentMeetService.unsetMeet();
    this.initialMeetFollowLoad = false;
  }

  open() {
    const offcanvasRef = this.offcanvasService.open(SidebarComponent);
    offcanvasRef.componentInstance.name = 'World';
  }

  protected readonly environment = environment;
}
